import DataTable from "@/components/data-table";
import { useDataTableSettings } from "@/components/data-table/hooks/useDataTableSettings";
import Spinner from "@/components/Spinner";
import { Card, CardContent } from "@/components/ui/card";
import { ReadonlyObject } from "@/types/readOnly";
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import { List } from "@/components/list/List";
import { ReadProjectStorePeriod } from "@/types/projectStorePeriod";

import { DataTableSettingsContextProvider } from "@/components/data-table/DataTableSettingsContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { SuccessFetchListResponse } from "@/services/api";
import { PlusIcon } from "lucide-react";
import { useProjectStoreContext } from "./ProjectStoreContext";
import { useDataTableQuery } from "@/components/data-table/hooks/useDataTableQuery";
import { PeriodsTable } from "@/modules/projects/components/PeriodsTable";
import { useSyncTableRowHover } from "@/modules/projects/hooks/useSyncTableRowHover";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useCheckIsProjectLocked } from "../hooks/useCheckIsProjectLocked";
import { useProjectStoreFormStore } from "../hooks/useProjectStoreFormStore";
import ProjectStoreSaveButton from "./ProjectStoreSaveButton";

export const ProjectStoreListContent = <T extends ReadonlyObject>() => {
  const { can_update } = useModulePermissions();
  const { handleSubmit, inFetching, setIsAddLimitsDialogOpen } =
    useProjectStoreContext();
  const { t } = useTranslation();
  const { query } =
    useDataTableQuery<SuccessFetchListResponse<ReadProjectStorePeriod>>();
  const settings = useDataTableSettings<T>();
  const isProjectLocked = useCheckIsProjectLocked();

  const removeStoresWithoutPeriods = useProjectStoreFormStore(
    (s) => s.removeStoresWithoutPeriods,
  );
  const setRemoveStoresWithoutPeriods = useProjectStoreFormStore(
    (s) => s.setRemoveStoresWithoutPeriods,
  );

  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const periodsTableContainerRef = useRef<HTMLDivElement | null>(null);

  const { onTableHover, onTableLeave } = useSyncTableRowHover(
    tableContainerRef,
    periodsTableContainerRef,
  );

  const onAddPeriods = useCallback(() => {
    setIsAddLimitsDialogOpen(true);
  }, [setIsAddLimitsDialogOpen]);

  const onSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      handleSubmit();
    },
    [handleSubmit],
  );

  const disabled = !can_update || isProjectLocked;

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-1 min-h-0">
      <ResizablePanelGroup
        direction="horizontal"
        className="gap-2"
        autoSaveId="project-store-list-content"
      >
        <ResizablePanel>
          <Card>
            <CardContent className="flex flex-col min-h-32 w-full p-1">
              {query.isLoading ? (
                <Spinner
                  className="h-full w-full grow"
                  label={t("table.loading")}
                />
              ) : (
                <>
                  <DataTableSettingsContextProvider value={settings}>
                    <DataTable
                      ref={tableContainerRef}
                      onRowMouseEnter={onTableHover}
                      onRowMouseLeave={onTableLeave}
                    />
                    <List.Status className="py-3 px-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={onAddPeriods}
                        disabled={disabled}
                      >
                        <PlusIcon className="w-4 h-4" />
                        {t("projects.menubar.store.addLimits")}
                      </Button>
                    </List.Status>
                  </DataTableSettingsContextProvider>
                </>
              )}
            </CardContent>
          </Card>
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel>
          <Card>
            <CardContent className="flex flex-col w-full min-h-32 p-1 @container">
              {query.isLoading ? (
                <Spinner
                  className="h-full w-full grow"
                  label={t("table.loading")}
                />
              ) : (
                <DataTableSettingsContextProvider value={settings}>
                  <PeriodsTable
                    ref={periodsTableContainerRef}
                    onRowMouseEnter={onTableHover}
                    onRowMouseLeave={onTableLeave}
                  />
                </DataTableSettingsContextProvider>
              )}
            </CardContent>
          </Card>
        </ResizablePanel>
      </ResizablePanelGroup>

      <footer className="[grid-area:footer] flex p-2 justify-end gap-6">
        <Label className="flex items-center gap-2">
          <Checkbox
            checked={removeStoresWithoutPeriods}
            onCheckedChange={setRemoveStoresWithoutPeriods}
            disabled={disabled}
          />
          {t("projects.projectStores.removeStoresWithoutPeriods")}
        </Label>
        <ProjectStoreSaveButton isLoading={inFetching} />
      </footer>
    </form>
  );
};
